import React from "react";
import ReactMarkdown from "react-markdown";
import { useTranslation } from "react-i18next";
import { Megaphone } from "lucide-react";

// ─────────────────────────────────────────────────────────────────────────────
// Contenido de un pop-up escrito por un operador (o por Beartrack).
//
// Seguridad: el texto lo escribe un TERCERO y se muestra en la sesión del
// comercio, así que:
//   - nunca se interpreta HTML (`skipHtml`): solo Markdown;
//   - los links solo pueden ser https, mailto o tel, y abren en otra pestaña
//     sin `opener`;
//   - las imágenes solo por https.
// El backend ya rechaza al guardar lo que no cumple; esto es la barrera al
// pintar, por si algo llegó por otra vía.
//
// Lo usa el portal del comercio (AnnouncementsHost) y la vista previa del
// editor, para que el autor vea EXACTAMENTE lo que verá el comercio.
// ─────────────────────────────────────────────────────────────────────────────

const LINK_PROTOCOLS = new Set(["https:", "mailto:", "tel:"]);

function parse(url) {
  try {
    return new URL(String(url));
  } catch {
    return null;
  }
}

/** URL de link permitida, o "" (react-markdown deja el link sin destino). */
export function safeLinkUrl(url) {
  const parsed = parse(url);
  if (!parsed || !LINK_PROTOCOLS.has(parsed.protocol)) return "";
  if (parsed.username || parsed.password) return "";
  return parsed.href;
}

/** URL de imagen permitida (solo https), o "". */
export function safeImageUrl(url) {
  const parsed = parse(url);
  if (!parsed || parsed.protocol !== "https:") return "";
  if (parsed.username || parsed.password) return "";
  return parsed.href;
}

// Exportados para las Novedades (components/productNews): mismo Markdown, misma
// barrera al pintar.
export function urlTransform(url, key) {
  return key === "src" ? safeImageUrl(url) : safeLinkUrl(url);
}

export const MARKDOWN_COMPONENTS = {
  a: ({ href, children }) =>
    href ? (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer nofollow"
        className="font-medium text-brand-600 underline underline-offset-2 hover:text-brand-700"
      >
        {children}
      </a>
    ) : (
      <span>{children}</span>
    ),
  img: ({ src, alt }) =>
    src ? (
      <img
        src={src}
        alt={alt || ""}
        loading="lazy"
        className="dark-safe-img mx-auto my-2 max-h-64 w-auto max-w-full rounded-lg"
      />
    ) : null,
  p: ({ children }) => <p className="leading-relaxed">{children}</p>,
  ul: ({ children }) => <ul className="list-disc space-y-1 pl-5">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal space-y-1 pl-5">{children}</ol>,
  h1: ({ children }) => <h4 className="text-base font-semibold text-fg">{children}</h4>,
  h2: ({ children }) => <h4 className="text-base font-semibold text-fg">{children}</h4>,
  h3: ({ children }) => <h5 className="text-sm font-semibold text-fg">{children}</h5>,
  blockquote: ({ children }) => (
    <blockquote className="border-l-2 border-border pl-3 text-fg-muted">{children}</blockquote>
  ),
};

/** Quién habla: el operador (con su logo si lo tiene) o Beartrack. */
function SenderLine({ sender }) {
  const { t } = useTranslation();
  const isPlatform = sender?.kind === "platform";
  const name = isPlatform ? "Beartrack" : sender?.name;
  return (
    <div className="flex items-center gap-2 text-xs text-fg-muted">
      {!isPlatform && sender?.logo ? (
        <img
          src={safeImageUrl(sender.logo) || undefined}
          alt=""
          className="dark-safe-img h-6 w-6 rounded object-contain"
        />
      ) : (
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-bg-muted">
          <Megaphone className="h-3.5 w-3.5" aria-hidden="true" />
        </span>
      )}
      <span>
        {name
          ? t("Mensaje de {{name}}", { name })
          : t("Mensaje de tu operador logístico")}
      </span>
    </div>
  );
}

/**
 * @param {{ announcement: {
 *   body?: string, imageUrl?: string|null, imageAlt?: string|null,
 *   sender?: { kind: "operator"|"platform", name?: string|null, logo?: string|null }
 * } }} props
 */
export default function RemoteAnnouncementContent({ announcement }) {
  const imageUrl = safeImageUrl(announcement?.imageUrl);
  return (
    <div className="flex w-full flex-col gap-3 text-left text-sm text-fg">
      <SenderLine sender={announcement?.sender} />
      {imageUrl && (
        <img
          src={imageUrl}
          alt={announcement?.imageAlt || ""}
          className="dark-safe-img max-h-72 w-full rounded-lg object-contain"
        />
      )}
      {announcement?.body ? (
        <div className="flex flex-col gap-2">
          <ReactMarkdown
            skipHtml
            urlTransform={urlTransform}
            components={MARKDOWN_COMPONENTS}
          >
            {announcement.body}
          </ReactMarkdown>
        </div>
      ) : null}
    </div>
  );
}
